import React, {useEffect, useRef, useState} from 'react';
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  EllipsisVerticalIcon as DotsVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon as XIcon
} from '@heroicons/react/24/solid';
import {FolderOutlined} from '@ant-design/icons';
import type {Note as NoteType} from '../types/note';
import type {Attachment} from '../types/attachment';
import {addAttachment, deleteAttachment} from '../api';
import {useDeleteNote, useUpdateNote} from '../hooks/useNoteMutations';
import './Note.css';
import {useLocation, useNavigate} from 'react-router-dom';
import {BACKEND_URL} from '../config';
import {NoteEditor} from './NoteEditor';
import ConfirmModal from './ConfirmModal';
import MoveNoteModal from './MoveNoteModal';
import {Editor, EditorContent} from '@tiptap/react';

interface NoteProps {
  note: NoteType;
  onUpdate: (note: NoteType) => void;
  onDelete: (id: number) => void;
  isEditing?: boolean;
  onViewNote?: () => void;
  simpleEdit?: boolean;
}

export default function Note({ note, onUpdate, onDelete, isEditing: propIsEditing, onViewNote, simpleEdit }: NoteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(propIsEditing ?? false);
  const [showEditMenu, setShowEditMenu] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>(note.attachments || []);
  const [isSaving, setIsSaving] = useState(false);
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'delete' | 'cancel' | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [selectedCategory, setSelectedCategory] = useState<number>(note.category_id);

  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowEditMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update attachments when note changes
  useEffect(() => {
    setAttachments(note.attachments || []);
  }, [note.attachments]);

  useEffect(() => {
    if (propIsEditing !== undefined) {
      setIsEditing(propIsEditing);
    }
  }, [propIsEditing]);

  const handleAddImage = async (file: File) => {
    try {
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      const attachment = await addAttachment(note.id, file, (event) => {
        if (Number.isFinite(event.total) && event.total! > 0) {
          setUploadProgress(prev => ({ ...prev, [file.name]: Math.round((event.loaded / event.total!) * 100) }));
        }
      });
      setAttachments(prev => [...prev, attachment]);
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      return `${BACKEND_URL}/${attachment.file_path}`;
    } catch (error) {
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      console.error('Error adding image:', error);
      return null;
    }
  };

  const handleAddFile = async (file: File) => {
    try {
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      const attachment = await addAttachment(note.id, file, (event) => {
        if (Number.isFinite(event.total) && event.total! > 0) {
          setUploadProgress(prev => ({ ...prev, [file.name]: Math.round((event.loaded / event.total!) * 100) }));
        }
      });
      setAttachments(prev => [...prev, attachment]);
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      console.error('Error adding file:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await deleteAttachment(attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const handleMoveNote = async (targetCategoryId: number) => {
    try {
      const updatedNote = await updateNoteMutation.mutateAsync({ 
        id: note.id, 
        note: { content: note.content, category_id: targetCategoryId }
      });
      onUpdate(updatedNote);
      navigate(`/notes?category=${targetCategoryId}`);
    } catch (error) {
      console.error('Failed to move note:', error);
    }
  };

  const handleSave = async (content: string) => {
    setIsSaving(true);
    try {
      const updatedNote = await updateNoteMutation.mutateAsync({
        id: note.id,
        note: { content, category_id: note.category_id }
      });
      onUpdate(updatedNote);
      setIsEditing(false);
      
      // If we're in full edit mode (URL contains /edit), always go to note list
      if (location.pathname.includes('/edit')) {
        navigate('/notes');
      }
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteNoteMutation.mutateAsync(note.id);
      onDelete(note.id);
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleCancelEdit = () => {
    setConfirmAction('cancel');
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (confirmAction === 'delete') {
      handleDelete();
    } else if (confirmAction === 'cancel') {
      setIsEditing(false);
      setShowEditMenu(false);
    }
    setShowConfirm(false);
    setConfirmAction(null);
  };

  const handleCancelModal = () => {
    setShowConfirm(false);
    setConfirmAction(null);
  };

  const handleEditorReady = (editor: Editor) => {
    setEditorInstance(editor);
  };

  return (
    <div className="note bg-white dark:bg-[#0b1d51] rounded-2xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl border border-[#048ba8]/10 relative">
      <div className="flex justify-between items-start mb-6">
        {/* Category path */}
        <div className="text-sm text-[#849469] dark:text-[#f4d35e] flex items-center gap-2">
          <FolderOutlined className="w-4 h-4 text-[#048ba8] dark:text-[#048ba8]" />
          {note.category_breadcrumb?.length > 0 ? (
            <div className="flex items-center gap-1">
              {note.category_breadcrumb.map((category, index) => (
                <div key={index} className="flex items-center">
                  {index > 0 && (
                    <ChevronRightIcon className="w-3 h-3 mx-1 text-[#048ba8] dark:text-[#048ba8]" />
                  )}
                  {category}
                </div>
              ))}
            </div>
          ) : (
            <span className="text-[#849469]/60 dark:text-[#f4d35e]/60 italic">Uncategorized</span>
          )}
        </div>
        {/* Actions */}
        <div className="note-actions flex items-center z-10">
          <div className="relative" ref={dropdownRef}>
            {isEditing ? (
              <div className="flex space-x-2">
                <button
                  onClick={handleCancelEdit}
                  className="btn-secondary bg-[#849469] hover:bg-[#849469]/90 text-white px-4 py-2 rounded-2xl transition-all duration-200 text-sm font-medium flex items-center gap-2"
                  type="button"
                >
                  <XIcon className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={() => {
                      handleSave(editorInstance ? editorInstance.getHTML() : '');
                  }}
                  className="btn-primary bg-[#048ba8] hover:bg-[#048ba8]/90 text-white px-4 py-2 rounded-2xl transition-all duration-200 text-sm font-medium flex items-center gap-2"
                  disabled={isSaving}
                >
                  <CheckIcon className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            ) : (
              <div className="relative inline-block">
                <button
                  onClick={() => setShowEditMenu(!showEditMenu)}
                  className="w-10 h-10 rounded-full bg-[#048ba8] hover:bg-[#048ba8]/90 text-white transition-all duration-200 flex items-center justify-center"
                  aria-label="Open menu"
                >
                  <DotsVerticalIcon className="w-5 h-5" />
                </button>
                {!isEditing && showEditMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#0b1d51] rounded-2xl shadow-lg py-1 z-50 border border-[#048ba8]/20">
                    {onViewNote && (
                      <button
                        onClick={() => {
                          onViewNote();
                          setShowEditMenu(false);
                        }}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#0b1d51] dark:text-white hover:bg-[#048ba8]/10 transition-colors duration-200"
                      >
                        <EyeIcon className="w-4 h-4" />
                        View Note
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        editorInstance?.setEditable(true);
                        setShowEditMenu(false);
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#0b1d51] dark:text-white hover:bg-[#048ba8]/10 transition-colors duration-200"
                    >
                      <PencilIcon className="w-4 h-4" />
                      Quick Edit
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/notes/${note.id}/edit`, {
                          state: {
                            from: {
                              pathname: location.pathname,
                              search: location.search,
                              state: {
                                page: location.state?.page
                              }
                            },
                            note
                          }
                        });
                        setShowEditMenu(false);
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#0b1d51] dark:text-white hover:bg-[#048ba8]/10 transition-colors duration-200"
                    >
                      <PencilIcon className="w-4 h-4" />
                      Full Edit
                    </button>
                    <button
                      onClick={() => {
                        setShowMoveModal(true);
                        setShowEditMenu(false);
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#0b1d51] dark:text-white hover:bg-[#048ba8]/10 transition-colors duration-200"
                    >
                      <ArrowRightIcon className="w-4 h-4" />
                      Move Note
                    </button>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowEditMenu(false);
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#d64933] hover:bg-[#d64933]/10 transition-colors duration-200"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete Note
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="editor-container rounded-2xl overflow-hidden border border-[#048ba8]/20 bg-white dark:bg-[#0b1d51]/50 p-4">
          <NoteEditor
            note={note}
            isSaving={isSaving}
            onSave={handleSave}
            onAddImage={handleAddImage}
            onAddFile={handleAddFile}
            showSaveButton={false}
            onCategoryChange={setSelectedCategory}
            selectedCategoryId={selectedCategory}
            onEditorReady={(editorInstance) => handleEditorReady(editorInstance)}
            editMode={isEditing}
          />
      </div>
      
      <div className="attachments mt-6 p-4 rounded-2xl bg-gray-50 dark:bg-[#0b1d51]/30 border border-[#048ba8]/10">
        <h3 className="text-base font-semibold mb-3 text-[#0b1d51] dark:text-white">Attachments</h3>
        {attachments.length === 0 && Object.keys(uploadProgress).length === 0 ? (
          <p className="text-gray-500 text-sm">No attachments</p>
        ) : (
          <div className="attachments-grid grid grid-cols-1 gap-2">
            {attachments.map(attachment => (
              <div key={attachment.id} className="flex items-center gap-2 py-1">
                {attachment.mime_type.startsWith('image/') ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <rect x="3" y="3" width="18" height="18" rx="2" fill="#e0f2fe" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17l4-4 4 4M8 13l4-4 4 4" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                <a 
                  href={`${BACKEND_URL}/${attachment.file_path}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-gray-700 truncate hover:underline"
                >
                  {attachment.filename}
                </a>
                {isEditing && (
                  <button
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    className="delete-button ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-[#d64933] text-white hover:bg-[#d64933]/90 transition-colors duration-200"
                    title="Delete attachment"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {isEditing && Object.entries(uploadProgress).map(([fileName, progress]) => (
              !attachments.some(att => att.filename === fileName) && (
                <div key={`uploading-${fileName}`} className="flex items-center gap-2 py-1 relative">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 text-teal-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-gray-700 truncate">{fileName}</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-lg overflow-hidden ml-2 flex items-center">
                    <div className="h-2 bg-teal-500 rounded-lg transition-all duration-200" style={{ width: `${progress}%` }} />
                    <span className="ml-2 text-xs text-gray-700" style={{ minWidth: '2.5rem' }}>{progress}%</span>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
      <div className="note-meta mt-6 text-right text-sm text-[#849469] space-y-1">
        <span className="note-date block flex items-center justify-end gap-2">
          <CalendarIcon className="w-4 h-4" />
          Created: {new Date(note.created_at).toLocaleString()}
        </span>
        <span className="note-date block flex items-center justify-end gap-2">
          <ClockIcon className="w-4 h-4" />
          Last updated: {new Date(note.updated_at).toLocaleString()}
        </span>
      </div>
      <ConfirmModal
        open={showConfirm}
        onCancel={handleCancelModal}
        onConfirm={handleConfirm}
        message={confirmAction === 'delete' ? 'Are you sure you want to delete this note?' : 'Are you sure you want to cancel editing? Any unsaved changes will be lost.'}
        confirmText={confirmAction === 'delete' ? 'Delete' : 'Yes, cancel'}
      />
      <MoveNoteModal
        isVisible={showMoveModal}
        onCancel={() => setShowMoveModal(false)}
        note={note}
        onSuccess={(updatedNote, categoryId) => {
          onUpdate(updatedNote);
          setShowMoveModal(false);
          if (categoryId !== note.category_id) {
            navigate(`/notes?category=${categoryId}`);
          }
        }}
      />
    </div>
  );
}